import * as React from 'react';
import { useContext, useEffect, useState, Dispatch } from 'react';
import { useTranslation } from 'react-i18next';

// Openlayers
import OlSourceVector from 'ol/source/Vector';
import Geometry from 'ol/geom/Geometry';
import OlFeature from 'ol/Feature';
import OlFormatWKT from 'ol/format/WKT';
import { ProjectionLike } from 'ol/proj';

// MUI
import {
  Autocomplete,
  Avatar,
  Box,
  Button,
  Chip,
  CircularProgress,
  FormControl,
  FormControlLabel,
  FormLabel,
  Icon,
  IconButton,
  InputLabel,
  List,
  ListItem,
  ListItemAvatar,
  ListItemText,
  ListSubheader,
  MenuItem,
  Radio,
  RadioGroup,
  Select,
  SelectChangeEvent,
  TextField,
  Tooltip,
  Typography,
  useTheme,
} from '@mui/material';

// MUI Icons
import ExploreIcon from '@mui/icons-material/Explore';
import { FilterAlt } from '@mui/icons-material';

// Context
import MapContext, { MapContextType } from '@/context/MapContext/MapContext';
import TenantContext, { TenantContextType } from '@/context/TenantContext/TenantContext';
import UserContext from '@/context/UserContext/UserContext';

// Types
import { DCRecord } from '@/@types/lib/dataController';
import useApi from '@/lib/api/useApi';

export interface AttributeSearchPaneProps {
  setSelectedSource: Dispatch<React.SetStateAction<OlSourceVector<OlFeature<Geometry>>>>;
}

type SearchAttributeOperator = {
  name: string; // service name we send to filter endpoint
  ttoken: string; // FE display name
};

type SearchAttributeFilter = {
  attribute: string; // service name we send to filter endpoint
  ttoken: string; // FE display name
  operators: SearchAttributeOperator[];
  type: 'numeric' | 'text' | 'boolean';
};

type SearchAttributeToponimTip = {
  id: number;
  naziv: string; // FE display name
  icon?: string;
  filters: SearchAttributeFilter[];
  max_zoom?: number;
};

type AddedFilter = {
  attribute: SearchAttributeFilter;
  operator: SearchAttributeOperator;
  value: string;
  toponimId: string;
};

const RESULT_LOAD_ITEM_SIZE = 20;

const AttributeSearchPane = (props: AttributeSearchPaneProps) => {
  const { setSelectedSource } = props;

  const { t } = useTranslation();
  const api = useApi();
  const mapContext = useContext(MapContext);
  const { map } = mapContext as MapContextType;
  // const dataContext = useContext(DataContext);
  // const { searchData, setSearchData } = dataContext as DataContextType;
  const tenantContext = useContext(TenantContext);
  const { mapSettings } = tenantContext as TenantContextType;

  const theme = useTheme();

  const [results, setResults] = useState<DCRecord[]>([]);
  const [resultsLoading, setResultsLoading] = useState(false);

  const [toponimTipovi, setToponimTipovi] = useState<SearchAttributeToponimTip[]>([]);
  const [tipoviLoading, setTipoviLoading] = useState(true);

  const [selectedToponimTipId, setSelectedToponimTipId] = useState<number | null>(null);
  const [selectedAttributeIndex, setSelectedAttributeIndex] = useState<number | ''>('');
  const [selectedOperatorIndex, setSelectedOperatorIndex] = useState<number | ''>('');

  const [itemDisplayLimit, setItemDisplayLimit] = useState<number>(RESULT_LOAD_ITEM_SIZE);

  const [value, setValue] = useState<number | string | boolean>('');

  const [addedFilters, setAddedFilters] = useState<AddedFilter[]>([]);

  const userContext = React.useContext(UserContext);

  const selectedToponimTip = React.useMemo(() => {
    if (selectedToponimTipId == null || !toponimTipovi.length) return null;
    return toponimTipovi.find((tip) => tip.id === selectedToponimTipId) ?? null;
  }, [selectedToponimTipId, toponimTipovi]);

  const selectedAttribute = React.useMemo(() => {
    if (selectedAttributeIndex === '' || !selectedToponimTip?.filters) return null;
    return selectedToponimTip.filters[selectedAttributeIndex] ?? null;
  }, [selectedToponimTip, selectedAttributeIndex]);

  const selectedAttributeOperator = React.useMemo(() => {
    if (selectedOperatorIndex === '' || !selectedAttribute?.operators) return null;
    return selectedAttribute.operators[selectedOperatorIndex] ?? null;
  }, [selectedAttribute, selectedOperatorIndex]);

  // Load toponim tipovi
  useEffect(() => {
    loadToponimTipovi();
  }, []);

  const loadToponimTipovi = React.useCallback(() => {
    api
      .get('core/attribute-search')
      .then((resp) => {
        if (resp.success && Array.isArray(resp.data)) {
          setToponimTipovi((resp.data as SearchAttributeToponimTip[]) || []);
          if (userContext !== null) {
            const existing = userContext.getRecent?.('attribute_search_toponim', 'toponimId', {}) || {};
            setSelectedToponimTipId(parseInt(existing, 10) || null);
          } else setSelectedToponimTipId((resp.data[0].id as number) || null);
        } else {
          setToponimTipovi([]);
        }
      })
      .catch(() => {
        setToponimTipovi([]);
        // console.warn(e);
      })
      .finally(() => {
        setTipoviLoading(false);
      });
  }, []);

  useEffect(() => {
    if (userContext !== null && selectedToponimTipId) {
      const filtersForToponim = userContext.getRecent?.('attribute_search', selectedToponimTipId.toString(), {}) || {};
      if (!filtersForToponim) return;
      const filters: AddedFilter[] = [];
      const toponimTip = toponimTipovi.find((tip) => tip.id === selectedToponimTipId);
      if (!toponimTip) return;

      Object.keys(filtersForToponim).forEach((attributeKey) => {
        const operatorsObj = filtersForToponim[attributeKey];
        if (!operatorsObj) return;

        const attributeDef = toponimTip.filters.find((f) => f.attribute === attributeKey);
        if (!attributeDef) return;

        Object.keys(operatorsObj).forEach((operatorKey) => {
          const operatorValue = operatorsObj[operatorKey];
          const operatorDef = attributeDef.operators.find((o) => o.name === operatorKey);
          if (!operatorDef) return;
          filters.push({
            attribute: attributeDef,
            operator: operatorDef,
            value: operatorValue.toString(),
            toponimId: Number(selectedToponimTipId).toString(),
          });
        })
      });
      setAddedFilters(filters);
    }
  }, [toponimTipovi, selectedToponimTipId]);

  useEffect(() => {
    setItemDisplayLimit(RESULT_LOAD_ITEM_SIZE);
    loadResults();
  }, [addedFilters]);

  const loadResults = React.useCallback(() => {
    if (addedFilters.length > 0) {
      api
        .post(
          `core/attribute-search/${selectedToponimTipId}`,
          addedFilters.map((fil) => ({
            value: fil.value,
            operator: fil.operator.name,
            attribute: fil.attribute.attribute,
          }))
        )
        .then((resp) => {
          if (resp.success && Array.isArray(resp.data)) {
            setResults(resp.data);
          } else {
            setResults([]);
          }
        })
        .catch(() => {
          setToponimTipovi([]);
          // console.warn(e);
        })
        .finally(() => {
          setResultsLoading(false);
        });
    } else {
      setResults([]);
      setResultsLoading(false);
    }
  }, [addedFilters, selectedToponimTipId]);

  const handleExplore = React.useCallback(
    (evt: React.MouseEvent<HTMLButtonElement>, item: DCRecord, toponim_id: number) => {
      const { wkt } = item;
      if (wkt) {
        const proj = item.proj ? (typeof item.proj === 'number' ? `EPSG:${item.proj}` : item.proj) : 'EPSG:3765';
        const wktFormatter = new OlFormatWKT();
        const feat = wktFormatter.readFeature(wkt, {
          // dataProjection: "EPSG:3765" // piece of shit code luka // sorry luka comment was made in the heat of the moment
          dataProjection: proj as ProjectionLike,
          featureProjection: 'EPSG:3857',
        });

        const searchSource = new OlSourceVector({
          features: [feat] as OlFeature<Geometry>[],
        });
        setSelectedSource(searchSource);

        const toponimTip = toponimTipovi.find((tpTip) => tpTip.id === toponim_id);

        setTimeout(() => {
          const extent = feat.getGeometry()?.getExtent();
          if (map) {
            const view = map.getView();
            if (extent)
              view.fit(extent, {
                padding: [10, 10, 10, 220],
                duration: 500,
                maxZoom: toponimTip?.max_zoom ? toponimTip.max_zoom : mapSettings?.map_max_view_zoom ? mapSettings.map_max_view_zoom : 20,
              });
          }
        }, 10);
      }
    },
    [map, toponimTipovi]
  );

  /* const icons: { [key: string]: React.ReactNode } = {
    '1': <HomeIcon color="primary" />,
    '2': <AccountBoxIcon color="primary" />,
    '3': <LanguageIcon color="primary" />,
    '4': <RequestPageIcon color="primary" />,
    default: <TravelExploreIcon color="primary" />,
  }; */

  const handleToponimChange = React.useCallback((event: React.ChangeEvent<HTMLInputElement>, newValue: string) => {
    if (userContext !== null) {
      userContext.setRecent('attribute_search_toponim', 'toponimId', newValue);
    }
    setSelectedToponimTipId(parseInt(newValue, 10)); // DOM Parses value as string so we cast it back
    setAddedFilters([]);
    setSelectedAttributeIndex('');
    setSelectedOperatorIndex('');
  }, []);

  const handleOperatorChange = React.useCallback((event: SelectChangeEvent<number | ''>) => {
    setSelectedOperatorIndex(event.target.value as number);
  }, []);

  const handleAddFilter = React.useCallback(() => {
    if (
      (value !== '' || selectedAttribute?.type === 'boolean') &&
      selectedAttribute &&
      selectedAttributeOperator &&
      selectedToponimTipId !== null
    ) {
      const newFilter = {
        attribute: selectedAttribute,
        operator: selectedAttributeOperator,
        value: value.toString(),
        toponimId: selectedToponimTipId.toString(),
      };
      const updated = addedFilters.some(
        (f) => f.attribute.attribute === newFilter.attribute.attribute && f.operator.name === newFilter.operator.name
      );
      if (updated) {
        setAddedFilters((prev) =>
          prev.map((f) =>
            f.attribute.attribute === newFilter.attribute.attribute && f.operator.name === newFilter.operator.name
              ? { ...f, value: newFilter.value }
              : f
          )
        );
      } else {
        setAddedFilters((prev) => {
          setResultsLoading(true);
          return [...prev, newFilter];
        });
      }

      if (userContext !== null) {
        const existing = userContext.getRecent?.('attribute_search', selectedToponimTipId.toString(), {}) || {};
        const attributeObject = existing[selectedAttribute.attribute];
        userContext.setRecent('attribute_search', selectedToponimTipId.toString(), {
          ...existing,
          [selectedAttribute.attribute]: {
            ...attributeObject,
            [selectedAttributeOperator.name]: value.toString(),
          },
        });
      }
      setSelectedOperatorIndex('');
      setSelectedAttributeIndex('');
      setValue('');
    }
  }, [value, selectedAttribute, selectedAttributeOperator]);

  const loadMoreItems = React.useCallback(() => {
    setItemDisplayLimit((prev) => Math.min(results.length, prev + RESULT_LOAD_ITEM_SIZE));
  }, [results]);

  const handleRemoveFilter = (indexToRemove: number) => {
    setAddedFilters((prev) => {
      const removedFilter = prev[indexToRemove];
      if (!removedFilter) return prev;

      const { toponimId } = removedFilter;
      const updated = prev.filter((_, ind) => ind !== indexToRemove);

      if (userContext !== null) {
        const attributeKey = removedFilter.attribute.attribute;
        const operatorKey = removedFilter.operator.name;

        const existing = userContext.getRecent?.('attribute_search', toponimId, {}) || {};
        const attributeObject = existing[attributeKey];
        const { [operatorKey]: operatorValue, ...rest } = attributeObject;

        if (Object.keys(rest).length === 0) {
          const raw = localStorage.getItem('attribute_search');
          if (raw) {
            const allAttributes = JSON.parse(raw);
            const selectedToponimEntry = allAttributes[toponimId];

            if (selectedToponimEntry) {
              delete selectedToponimEntry[attributeKey];
              if (Object.keys(selectedToponimEntry).length === 0) {
                delete allAttributes[toponimId];
              }

              localStorage.setItem('attribute_search', JSON.stringify(allAttributes));
            }
          }

          return updated;
        }

        userContext.setRecent('attribute_search', toponimId.toString(), {
          ...existing,
          [attributeKey]: rest,
        });
      }

      return updated;
    });
  };

  // #endregion

  return (
    <>
      {toponimTipovi.length ? (
        <Box display="flex" flexDirection="column" p={1} gap={1} width="100%">
          <FormControl>
            <FormLabel id="attribute-search-form-label">{t('map:labels.attribute_search.choose_search_option')}</FormLabel>
            <RadioGroup
              aria-labelledby="attribute-search-form-label"
              name="attribute-search-cbuttons-group"
              value={selectedToponimTipId}
              onChange={handleToponimChange}
            >
              {toponimTipovi.map((tip) => (
                <FormControlLabel value={tip.id.toString()} control={<Radio />} label={t(tip.naziv as string)} />
              ))}
            </RadioGroup>
          </FormControl>
          {selectedToponimTip ? (
            <>
              <Box display="flex" gap={1}>
                <FormControl fullWidth>
                  <Autocomplete
                    id="attribute-autocomplete"
                    options={selectedToponimTip.filters.map((filter, index) => ({ label: t(filter.ttoken), index }))}
                    value={
                      selectedAttributeIndex !== ''
                        ? { label: t(selectedToponimTip.filters[selectedAttributeIndex].ttoken), index: selectedAttributeIndex }
                        : null
                    }
                    onChange={(event, newValue) => {
                      if (newValue) {
                        setSelectedAttributeIndex(newValue.index);
                        setSelectedOperatorIndex('');
                      } else {
                        setSelectedAttributeIndex('');
                      }
                    }}   
                    renderInput={(params) => <TextField {...params} label={t('core:common.attribute')} />}
                  />
                </FormControl>

                <FormControl fullWidth>
                  <InputLabel id="operator-select-label">{t('core:common.operator')}</InputLabel>
                  <Select
                    disabled={!selectedAttribute}
                    labelId="operator-select-label"
                    label={t('core:common.operator')}
                    id="operator-select"
                    value={selectedOperatorIndex}
                    onChange={handleOperatorChange}
                  >
                    {selectedAttribute?.operators.map((op, index) => (
                      <MenuItem value={index}>{t(op.ttoken)}</MenuItem>
                    ))}
                  </Select>
                </FormControl>

                {!selectedAttribute || selectedAttribute?.type === 'text' ? (
                  <TextField
                    label={t('core:common.value')}
                    disabled={!selectedAttributeOperator}
                    onChange={(e) => setValue(e.target.value)}
                    fullWidth
                  />
                ) : null}
                {selectedAttribute?.type === 'numeric' ? (
                  <TextField
                    type="number"
                    label={t('core:common.value')}
                    disabled={!selectedAttributeOperator}
                    onChange={(e) => {
                      setValue(Number(e.target.value));
                    }}
                    fullWidth
                  />
                ) : null}
              </Box>
              <Box>
                <Button
                  variant="contained"
                  disabled={value === null || !selectedAttribute || !selectedAttributeOperator}
                  onClick={handleAddFilter}
                >
                  {t('core:buttons.add')}
                </Button>
              </Box>
            </>
          ) : null}
          {addedFilters.length ? (
            <Box width="100%" display="flex" flexWrap="wrap" gap={1}>
              {addedFilters.map((fil, index) => (
                <Chip
                  onDelete={() => handleRemoveFilter(index)}
                  color="success"
                  icon={<FilterAlt />}
                  label={`${t(fil.attribute.ttoken)} ${t(fil.operator.ttoken)} ${fil.value}`}
                />
              ))}
            </Box>
          ) : null}
        </Box>
      ) : tipoviLoading ? (
        <Box display="flex" width="100%" justifyContent="center" alignItems="center">
          <CircularProgress sx={{ margin: 2 }} />
        </Box>
      ) : (
        <Typography sx={{ m: 1 }}>{t('map:labels.attribute_search.no_filters_found')}</Typography>
      )}
      {resultsLoading ? (
        <Box display="flex" width="100%" height="100%" justifyContent="center" alignItems="center">
          <CircularProgress />
        </Box>
      ) : results.length ? (
        <>
          <List
            sx={{
              width: '100%',
              height: '100%',
              bgcolor: 'background.paper',
              position: 'relative',
              overflow: 'auto',
              '& ul': { padding: 0 },
            }}
            subheader={<li />}
          >
            {selectedToponimTip ? (
              <li key={`section-${selectedToponimTip.id}`}>
                <ul>
                  <ListSubheader>{`${t(selectedToponimTip.naziv)} (${results.length})`}</ListSubheader>
                  {results.slice(0, itemDisplayLimit).map((item) => (
                    <ListItem
                      key={`item-${selectedToponimTip.id}-${item.id}`}
                      secondaryAction={
                        <Tooltip title={t('map:controls.zoom_to_selected')}>
                          <IconButton edge="end" aria-label="explore" onClick={(evt) => handleExplore(evt, item, selectedToponimTip.id)}>
                            <ExploreIcon color="primary" />
                          </IconButton>
                        </Tooltip>
                      }
                    >
                      <ListItemAvatar>
                        <Avatar
                          sx={{
                            backgroundColor: theme.palette.background.default,
                          }}
                        >
                          <Icon color="disabled">{selectedToponimTip.icon}</Icon>
                        </Avatar>
                      </ListItemAvatar>
                      <ListItemText primary={`${item.naziv}`} secondary={`${item.opis}`} />
                    </ListItem>
                  ))}
                </ul>
              </li>
            ) : null}
          </List>
          {itemDisplayLimit < results.length ? (
            <Button
              onClick={() => loadMoreItems()}
              variant="contained"
              size="small"
              sx={{
                width: '80%',
                textAlign: 'center',
                transform: 'translate(10%,0)',
                my: 1,
              }}
            >
              {t('buttons.show_more')}
            </Button>
          ) : null}
        </>
      ) : addedFilters.length ? (
        <Typography sx={{ m: 1 }}>{t('map:labels.attribute_search.no_results')}</Typography>
      ) : null}
    </>
  );
};

export default AttributeSearchPane;
